/* ============================================================
   Bimmer House — global.js
   Site-wide behaviour. Vanilla JS, no dependencies, modular.
   Re-initialises cleanly on Theme Editor events so every section
   keeps working after live reloads.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Cart + toast (shared API) ---------------------- */
  var BH = window.BimmerHouse = window.BimmerHouse || {};
  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  BH.toast = function (msg) {
    var el = document.getElementById('bh-toast');
    var msgEl = document.getElementById('bh-toast-msg');
    if (!el || !msgEl) return;
    msgEl.textContent = msg;
    el.hidden = false;
    clearTimeout(BH._toastTimer);
    BH._toastTimer = setTimeout(function () { el.hidden = true; }, 2400);
  };

  BH.refreshCartCount = function () {
    return fetch(routes + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (n) {
          n.textContent = cart.item_count;
        });
        return cart;
      })
      .catch(function () {});
  };

  BH.addToCart = function (items, announce) {
    var payload = Array.isArray(items) ? { items: items } : { items: [items] };
    return fetch(routes + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function (res) {
        BH.refreshCartCount();
        BH.toast(announce || 'Added to cart');
        document.dispatchEvent(new CustomEvent('bh:cart:added', { detail: res }));
        return res;
      })
      .catch(function (err) {
        BH.toast((err && err.description) || 'Could not add to cart');
        throw err;
      });
  };

  /* ---------- Component initialisers ------------------------- */

  function initHeader(scope) {
    var header = (scope || document).querySelector('[data-header]');
    if (!header || header.dataset.bhBound) return;
    header.dataset.bhBound = '1';
    if (header.dataset.transparent !== 'true') return;
    var solid = function () {
      header.classList.toggle('bh-header--solid', window.scrollY > 20);
    };
    solid();
    window.addEventListener('scroll', solid, { passive: true });
  }

  function initMobileMenu(scope) {
    (scope || document).querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      if (btn.dataset.bhBound) return;
      btn.dataset.bhBound = '1';
      var menu = document.getElementById(btn.getAttribute('aria-controls'));
      if (!menu) return;
      var setOpen = function (open) {
        menu.classList.toggle('bh-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.style.overflow = open ? 'hidden' : '';
        if (open) { var f = menu.querySelector('a,button'); if (f) f.focus(); }
      };
      btn.addEventListener('click', function () { setOpen(!menu.classList.contains('bh-open')); });
      menu.querySelectorAll('[data-menu-close]').forEach(function (c) {
        c.addEventListener('click', function () { setOpen(false); btn.focus(); });
      });
      menu.addEventListener('keydown', function (e) { if (e.key === 'Escape') { setOpen(false); btn.focus(); } });
    });
  }

  function initReveal(scope) {
    var els = (scope || document).querySelectorAll('.bh-reveal:not([data-bh-revealed])');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('bh-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('bh-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(function (el) { el.dataset.bhRevealed = '1'; io.observe(el); });
  }

  function initQuickAdd(scope) {
    (scope || document).querySelectorAll('[data-quick-add]:not([data-bh-bound])').forEach(function (btn) {
      btn.dataset.bhBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.getAttribute('data-variant-id');
        if (!id) return;
        var label = btn.getAttribute('data-product-title') || 'Item';
        var original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        BH.addToCart({ id: id, quantity: 1 }, label + ' added to cart')
          .catch(function () {})
          .finally(function () { btn.disabled = false; btn.textContent = original; });
      });
    });
  }

  function initAnnouncement(scope) {
    (scope || document).querySelectorAll('[data-announcement]:not([data-bh-bound])').forEach(function (bar) {
      bar.dataset.bhBound = '1';
      var items = bar.querySelectorAll('[data-announcement-item]');
      if (items.length < 2) return;
      var i = 0;
      var speed = (parseInt(bar.getAttribute('data-rotate'), 10) || 5) * 1000;
      setInterval(function () {
        items[i].hidden = true;
        i = (i + 1) % items.length;
        items[i].hidden = false;
      }, speed);
    });
  }

  function initVideoEmbeds(scope) {
    (scope || document).querySelectorAll('[data-video-embed]:not([data-bh-bound])').forEach(function (btn) {
      btn.dataset.bhBound = '1';
      btn.addEventListener('click', function () {
        var src = btn.getAttribute('data-embed');
        if (!src) return;
        var f = document.createElement('iframe');
        f.src = src;
        f.allow = 'autoplay; fullscreen; picture-in-picture';
        f.setAttribute('allowfullscreen', '');
        f.title = 'Video';
        btn.replaceWith(f);
      });
    });
  }

  function initAll(scope) {
    initHeader(scope);
    initMobileMenu(scope);
    initReveal(scope);
    initQuickAdd(scope);
    initAnnouncement(scope);
    initVideoEmbeds(scope);
  }

  /* ---------- Boot + Theme Editor lifecycle ------------------ */
  if (document.readyState !== 'loading') initAll(document);
  else document.addEventListener('DOMContentLoaded', function () { initAll(document); });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function () { /* observers self-clean on reload */ });

  BH.initAll = initAll;
})();
